// External
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';

// Internal
import { PrivacyService } from '../process/privacy/privacy.service';
import { NavigationService } from '../../navigation/navigation.service';
import { ConfigService } from '../../config/config.service';
import { ValuesService } from '../../values/values.service';
import { QueryParamsService } from '../core/query.params.service';
import { SubscriptionsService } from '../process/subscriptions/subscriptions.service';

@Injectable({ providedIn: 'root' })

export class PrivacyGuard {
    private readonly onDestroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly navigationService:     NavigationService,
        private readonly privacyService:        PrivacyService,
        private readonly router:                Router,
        private readonly configService:         ConfigService,
        private readonly valuesService:         ValuesService,
        private readonly queryParamsService:    QueryParamsService,
        private readonly subscriptionsService:  SubscriptionsService
    ) { }

    computePrivacyModulesRigths() {
        const hasActivity = this.configService.config.privacyTabs.activity;
        this.configService.setPrivacyActivity(hasActivity);

        const hasFootprint = this.configService.config.privacyTabs.footprint
            && this.privacyService.hasFootprintModule();
        this.configService.setPrivacyFootprint(hasFootprint);

        const hasBreaches = this.configService.config.privacyTabs.breaches
            && this.privacyService.hasBreachesModule();
        this.configService.setPrivacyBreaches(hasBreaches);

        const hasImpersonations = this.configService.config.privacyTabs.impersonations
            && this.privacyService.hasImpersonationsModule();
        this.configService.setPrivacyImpersonations(hasImpersonations);

        const hasBrokers = this.configService.config.privacyTabs.brokers
            && this.privacyService.hasBrokersModule();
        this.configService.setPrivacyBrokers(hasBrokers);

        const hasEducation = this.configService.config.privacyTabs.education
            && this.privacyService.hasEducationModule();
        this.configService.setPrivacyEducation(hasEducation);

        const hasHistory = this.configService.config.privacyTabs.history;
        this.configService.setPrivacyHistory(hasHistory);

        const hasMonitor = this.configService.config.privacyTabs.monitor;
        this.configService.setPrivacyMonitor(hasMonitor);

        const hasRemedies = this.configService.config.privacyTabs.remedies;
        this.configService.setPrivacyRemedies(hasRemedies);
    }

    /**
     * Method that decides if landing page is active
     * @returns {boolean} if landing page is active or not
     */
    private _decideIfLandingPageIsActive(): boolean {
        const hasDataPrivacy = this.subscriptionsService.hasDataPrivacy();
        const hasDataPrivacyExpired = this.subscriptionsService.hasDataPrivacyExpired();
        const canShowPrivacy = this.privacyService.canShowPrivacyContent();

        return !canShowPrivacy && (!hasDataPrivacy || (hasDataPrivacyExpired && !this.privacyService.getProvidedIdentity()));
    }

    /**
     * Generate an array with available paths in order of priority
     * Available paths can be turned off from config.service or based on specific use cases
     *
     * @private
     * @param {*} route
     * @param {*} state
     * @return {*}  {(Observable<UrlTree|boolean>)}
     */
    private _decideMainPrivacyRoute(route, state): UrlTree|boolean {
        const parkedIdentity = this.privacyService.getParkedIdentityStatus();
        const landingPageActive = this._decideIfLandingPageIsActive();
        const urlProperties = this.navigationService.processUrl(route, state);
        const redirect = [];
        const path = urlProperties.entirePath;
        let isArticlePage = false;
        const canLoadActivity = (this.subscriptionsService.hasDataPrivacy() || this.subscriptionsService.hasDataPrivacyExpired);

        if (canLoadActivity) {
            redirect.push(this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.activity.path));
        }

        if (this.configService.getPrivacyFootprint() && !parkedIdentity && !landingPageActive) {
            redirect.push(this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.footprint.path));
        }

        if (this.configService.getPrivacyBreaches() && !parkedIdentity && !landingPageActive) {
            redirect.push(this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.breaches.path));
        }

        if (this.configService.getPrivacyImpersonations() && !parkedIdentity && !landingPageActive) {
            redirect.push(this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.impersonations.path));
        }

        if (this.configService.getPrivacyBrokers() && !parkedIdentity && !landingPageActive) {
            redirect.push(this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.brokers.path));
        }

        if (this.configService.getPrivacyEducation() && !parkedIdentity && !landingPageActive) {
            redirect.push(this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.education.path));
            isArticlePage = this._decideIfArticlePage(path);
        }

        if (this.configService.getPrivacyHistory() && !parkedIdentity && !landingPageActive) {
            redirect.push(this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.history.path));
        }

        if (this.configService.getPrivacyMonitor() && !parkedIdentity && !landingPageActive) {
            redirect.push(this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.monitor.path));
        }

        if (this.configService.getPrivacyRemedies() && !parkedIdentity && !landingPageActive) {
            redirect.push(this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.remedies.path));
        }

        if (redirect.indexOf(`/${urlProperties.pathPieces[0]}/${urlProperties.pathPieces[1]}`) === -1 && !isArticlePage) {
            if (!redirect.length) {
                return this.router.parseUrl('/');
            }
            return this.router.parseUrl(`/${redirect[0]}`);
        }

        return false;
    }

    /**
     * Method that verifies if current page is a valid article page
     * @param {string} current path
     * @returns {boolean} is page is valid or not
     */
    private _decideIfArticlePage(path: string): boolean {
        const articlePath = this.valuesService.centralPaths.privacy.path.concat(this.valuesService.centralPaths.privacy.education.path,
                                                                                this.valuesService.centralPaths.privacy.education.article.path);
        if (path.indexOf(articlePath) === 0) {
           return true;
        }

        return false;
    }

    /**
     * Method that verifies if current page has query params and redirect cu referral page
     * @returns {UrlTree | boolean} the new page or true if no referral
     */
    private _checkRedirectAfterEmailConnection(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): UrlTree|boolean {
        const code = this.queryParamsService.get(this.valuesService.queryParams.code);
        const state = this.queryParamsService.get(this.valuesService.queryParams.state);

        if (code && state) {
            const urlProperties = this.navigationService.processUrl(_route, _state);
            //iau url-ul din state
            let referral = '';
            const stateParts: Array<string> = state.split('&');
            for (const index in stateParts) {
                if (stateParts[index].split('=')[0] === this.valuesService.queryParams.referral) {
                    referral = decodeURIComponent(stateParts[index].split('=')[1]);
                    if (referral === urlProperties.entirePath) {
                        //suntem pe acelasi url, nu are sens redirectul
                        return false;
                    } else {
                        //suntem pe alt url, sterg referral-ul din url si las ceilalti param
                        delete stateParts[index];
                        break;
                    }
                }
            }
            if (referral) {
                return this.router.parseUrl(referral);
            }
        }

        return false;
    }

    /**
     * Function that decides if the current route will be activated or it will be redirected to a new route
     * - daca nu exista identitate, trebuie sa vezi empty state-ul din tabul activity
     * - daca nu ai drepturi sa vezi un tab, esti redirectat in tabul activity
     * - daca esti deja in tabul activity nu mai trebuie redirect
     * @param {ActivatedRouteSnapshot} current route info
     * @param {RouterStateSnapshot} current state of the router
     * @returns {Observable<boolean|UrlTree>} true if route is allowed or a UrlTree that has the new route to which will be redirected
     */
    canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return new Observable(subscriber => {
            this.privacyService.listProvidedIdentity()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: () => {
                    this.computePrivacyModulesRigths();

                    const redirected = this._checkRedirectAfterEmailConnection(_route, _state);
                    if (!redirected) {
                        const newRoute = this._decideMainPrivacyRoute(_route, _state);
                        if (newRoute !== false) {
                            subscriber.next(newRoute);
                            subscriber.complete();
                        } else {
                            subscriber.next(true);
                            subscriber.complete();
                        }
                    } else {
                        subscriber.next(redirected);
                        subscriber.complete();
                    }
                },
                error: () => {
                    subscriber.next(true);
                    subscriber.complete();
                }
            });
        });
    }

}
